"use client";

import { useState, useEffect, useCallback } from "react";
import { RefreshCw } from "lucide-react";
import { useTranslations } from "next-intl";

export default function PWAUpdateNotification() {
  const t = useTranslations("PWA");
  const [showUpdate, setShowUpdate] = useState(false);
  const [registration, setRegistration] =
    useState<ServiceWorkerRegistration | null>(null);

  useEffect(() => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;

    const handleControllerChange = () => {
      window.location.reload();
    };

    navigator.serviceWorker.addEventListener(
      "controllerchange",
      handleControllerChange
    );

    navigator.serviceWorker.ready.then((reg) => {
      setRegistration(reg);

      if (reg.waiting) {
        setShowUpdate(true);
        return;
      }

      reg.addEventListener("updatefound", () => {
        const newWorker = reg.installing;
        if (!newWorker) return;

        newWorker.addEventListener("statechange", () => {
          if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
            setShowUpdate(true);
          }
        });
      });
    });

    return () => {
      navigator.serviceWorker.removeEventListener(
        "controllerchange",
        handleControllerChange
      );
    };
  }, []);

  const handleUpdate = useCallback(() => {
    if (registration?.waiting) {
      registration.waiting.postMessage({ type: "SKIP_WAITING" });
    }
    setShowUpdate(false);
  }, [registration]);

  if (!showUpdate) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-[9999] animate-slide-up sm:left-auto sm:right-4 sm:max-w-sm">
      <div className="material-thick flex items-center gap-3 rounded-2xl p-4 shadow-2xl">
        <RefreshCw className="h-5 w-5 shrink-0 text-[var(--apple-blue)]" />
        <p className="min-w-0 flex-1 text-sm text-[var(--label)]">
          {t("update_available")}
        </p>
        <button
          onClick={handleUpdate}
          className="shrink-0 rounded-xl bg-[var(--apple-blue)] px-4 py-2 text-xs font-semibold text-white active:opacity-80"
        >
          {t("update_button")}
        </button>
      </div>
    </div>
  );
}
